import {
  EyeOff,
  FileText,
  Flame,
  Heading,
  HelpCircle,
  History,
  Languages,
  LogIn,
  MessageSquare,
  Palette,
  Plus,
  Scale,
  Settings as SettingsIcon,
  Zap,
} from "lucide-react";
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ComponentType,
  type MutableRefObject,
  type ReactNode,
} from "react";

import { BRAND } from "@bigpp/config/brand";
import { Alert } from "@bigpp/ui/components/alert";
import { Button } from "@bigpp/ui/components/button";
import { Tooltip } from "@bigpp/ui/components/tooltip";
import { cn } from "@bigpp/ui/lib/utils";

import { useAgentRun } from "../agent/useAgentRun.ts";
import { openAuthDialog } from "../auth-handoff.ts";
import { env } from "../env.ts";
import {
  computeLocalDataSize,
  formatBytes,
  LOCAL_DATA_WARN_BYTES,
} from "../local-data.ts";
import { copyDebugLog } from "../log.ts";
import { runInSandbox } from "../bridge/controller.ts";
import { useTheme } from "../theme.ts";
import { trpc } from "../trpc.ts";
import { MOD_KEY } from "../platform.ts";
import { CommandPalette } from "./CommandPalette.tsx";
import { Composer, type AttachFilesFn } from "./Composer.tsx";
import { ConfirmDialog } from "./ConfirmDialog.tsx";
import { KeymapOverlay } from "./KeymapOverlay.tsx";
import { startOnboarding, useOnboardingOnFirstOpen } from "./Onboarding.tsx";
import { SettingsDialog } from "./SettingsDialog.tsx";
import { Suggestion, Suggestions } from "@bigpp/ui/components/suggestion";
import { MessageBubble } from "./MessageBubble.tsx";
import { SkillsPopover } from "./SkillsPopover.tsx";
import { ThreadsDialog } from "./ThreadsDialog.tsx";

export function TaskPane() {
  const me = trpc.me.useQuery(undefined, { retry: false });
  const [signInError, setSignInError] = useState<string | null>(null);
  const [signingIn, setSigningIn] = useState(false);
  useTheme(); // apply persisted theme on boot

  useEffect(() => {
    // Dev affordance: poke the bridge from devtools as `bigpp.run(...)`.
    Object.assign(window, { bigpp: { run: runInSandbox } });
    return () => {
      Object.assign(window, { bigpp: undefined });
    };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && (e.key === "d" || e.key === "D")) {
        e.preventDefault();
        void copyDebugLog().then((ok) => {
          console.log(`[bigpp] copyDebugLog ${ok ? "ok" : "failed"}`);
        });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
    };
  }, []);

  const handleSignIn = () => {
    setSigningIn(true);
    setSignInError(null);
    void openAuthDialog().then((result) => {
      setSigningIn(false);
      if (!result.ok) {
        setSignInError(result.error);
        return;
      }
      void me.refetch();
    });
  };

  if (me.isLoading) {
    return (
      <Pane>
        <div className="flex h-full items-center justify-center text-sm text-[var(--color-muted-foreground)]">
          Loading…
        </div>
      </Pane>
    );
  }

  if (me.error || !me.data) {
    return (
      <Pane>
        <div className="flex h-full flex-col items-start justify-center gap-3">
          <div className="flex flex-col gap-3">
            <img src="/logo.png" alt="" width={140} height={140} className="select-none" />
            <div>
              <h1 className="text-xl font-semibold tracking-tight">{BRAND}</h1>
              <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
                Sign in to start using {BRAND} in your decks.
              </p>
            </div>
          </div>
          <Button onClick={handleSignIn} disabled={signingIn}>
            <LogIn />
            {signingIn ? "Signing in…" : "Sign in"}
          </Button>
          <p className="text-xs text-[var(--color-muted-foreground)]">
            No account?{" "}
            <a
              href={`${env.VITE_WEB_URL}/auth/sign-up`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[var(--color-primary)] hover:underline"
            >
              Create one
            </a>
          </p>
          {signInError && <Alert variant="destructive">{signInError}</Alert>}
        </div>
      </Pane>
    );
  }

  return (
    <AuthedApp
      displayName={me.data.name?.trim() || me.data.email}
      email={me.data.email}
      credits={me.data.credits}
    />
  );
}

function AuthedApp({
  displayName,
  email,
  credits,
}: {
  displayName: string;
  email: string;
  credits: number;
}) {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [threadsOpen, setThreadsOpen] = useState(false);
  const [localDataWarn, setLocalDataWarn] = useState<{
    bytes: number;
    snapshots: number;
  } | null>(null);
  const attachFilesRef = useRef<AttachFilesFn | null>(null);
  const run = useAgentRun();
  useOnboardingOnFirstOpen(true);

  useEffect(() => {
    let cancelled = false;
    void computeLocalDataSize().then((usage) => {
      if (cancelled) return;
      if (usage.bytes >= LOCAL_DATA_WARN_BYTES) setLocalDataWarn(usage);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Pane
      fileDrop={{
        disabled: run.status.kind === "running",
        onFiles: (files) => {
          attachFilesRef.current?.(files);
        },
      }}
    >
      <Header
        displayName={displayName}
        onNewChat={run.newChat}
        onOpenHistory={() => {
          setThreadsOpen(true);
        }}
        onOpenSettings={() => {
          setSettingsOpen(true);
        }}
        onStartTour={startOnboarding}
      />
      <ChatPanel run={run} attachFilesRef={attachFilesRef} />
      <SettingsDialog
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        displayName={displayName}
        email={email}
        credits={credits}
      />
      <ThreadsDialog
        open={threadsOpen}
        onOpenChange={setThreadsOpen}
        activeThreadId={run.activeThreadId}
        onPick={(id) => {
          void run.loadThread(id);
        }}
      />
      <ConfirmDialog
        open={localDataWarn !== null}
        onOpenChange={(open) => {
          if (!open) setLocalDataWarn(null);
        }}
        title="Version history is using a lot of space"
        description={
          localDataWarn
            ? `Cached deck snapshots are using ${formatBytes(localDataWarn.bytes)} on this device (${localDataWarn.snapshots} snapshot${localDataWarn.snapshots === 1 ? "" : "s"}). Open Settings and clear local data if you want to free that space.`
            : undefined
        }
        confirmLabel="Open settings"
        cancelLabel="Not now"
        onConfirm={() => {
          setLocalDataWarn(null);
          setSettingsOpen(true);
        }}
      />
    </Pane>
  );
}

function Header({
  displayName,
  onNewChat,
  onOpenHistory,
  onOpenSettings,
  onStartTour,
}: {
  displayName: string;
  onNewChat: () => void;
  onOpenHistory: () => void;
  onOpenSettings: () => void;
  onStartTour: () => void;
}) {
  return (
    <header className="flex items-center justify-between gap-2 border-b border-[var(--color-border)] pb-1.5 mb-1.5">
      <div className="flex items-center gap-1.5 min-w-0">
        <img src="/logo.png" alt="" width={28} height={28} className="size-7 shrink-0 select-none" />
        <div className="flex flex-col leading-none min-w-0 gap-0.5">
          <h1 className="text-xs font-semibold tracking-tight select-none">{BRAND}</h1>
          <span className="text-[10px] text-[var(--color-muted-foreground)] truncate max-w-[140px]">
            {displayName}
          </span>
        </div>
      </div>
      <div className="flex items-center gap-0.5 shrink-0">
        <Tooltip label="Replay tour">
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={onStartTour}
            aria-label="Replay tour"
          >
            <HelpCircle />
          </Button>
        </Tooltip>
        <Tooltip label="New chat">
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={onNewChat}
            aria-label="New chat"
            data-tour="new-chat"
          >
            <Plus />
          </Button>
        </Tooltip>
        <Tooltip label="Chat history">
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={onOpenHistory}
            aria-label="Chat history"
          >
            <History />
          </Button>
        </Tooltip>
        <Tooltip label="Settings">
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={onOpenSettings}
            aria-label="Settings"
            data-tour="settings"
          >
            <SettingsIcon />
          </Button>
        </Tooltip>
      </div>
    </header>
  );
}

function ProgressBar({ running }: { running: boolean }) {
  return (
    <div
      className={cn(
        "pointer-events-none absolute left-0 right-0 top-0 h-0.5 overflow-hidden transition-opacity duration-300",
        running ? "opacity-100" : "opacity-0",
      )}
      aria-hidden
    >
      <div className="h-full w-1/3 rounded-full bg-[var(--color-primary)] animate-progress-slide" />
    </div>
  );
}

function ChatPanel({
  run,
  attachFilesRef,
}: {
  run: ReturnType<typeof useAgentRun>;
  attachFilesRef: MutableRefObject<AttachFilesFn | null>;
}) {
  const { messages, status, send, stop, pinnedSkillIds, setPinnedSkillIds } = run;
  const running = status.kind === "running";
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const stickToBottomRef = useRef(true);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [keymapOpen, setKeymapOpen] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
      if ((e.metaKey || e.ctrlKey) && e.key === "/") {
        e.preventDefault();
        setKeymapOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
    };
  }, []);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    stickToBottomRef.current = nearBottom;
  };

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (stickToBottomRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages]);

  const lastUserMsg =
    [...messages].reverse().find((m) => m.role === "user") ?? null;
  const canRetry = status.kind === "error" && lastUserMsg !== null;

  return (
    <div className="relative flex flex-1 min-h-0 flex-col gap-2">
      <ProgressBar running={running} />
      <div
        ref={scrollRef}
        onScroll={onScroll}
        className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-3 pr-1"
      >
        {messages.length === 0 && (
          <EmptyState
            onPick={(prompt) => {
              void send(prompt, []);
            }}
            disabled={running}
          />
        )}
        {messages.map((m) => (
          <MessageBubble
            key={m.id}
            msg={m}
            onFollowUp={(text) => {
              void send(text, []);
            }}
            onAnswerAskUser={run.answerAskUser}
          />
        ))}
        {status.kind === "error" && (
          <Alert variant="destructive">
            <div className="flex items-start justify-between gap-2">
              <span className="flex-1">{status.message}</span>
              {canRetry && lastUserMsg && (
                <button
                  type="button"
                  className="text-xs underline shrink-0"
                  onClick={() => {
                    void send(lastUserMsg.content, []);
                  }}
                >
                  Retry
                </button>
              )}
            </div>
          </Alert>
        )}
      </div>
      <div data-tour="composer">
        <Composer
          running={running}
          attachFilesRef={attachFilesRef}
          onSend={(text, attachments) => {
            void send(text, attachments);
          }}
          onStop={stop}
          leftSlot={
            <>
              <Tooltip label="Quick prompts (⌘K)">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => {
                    setPaletteOpen(true);
                  }}
                  disabled={running}
                  aria-label="Quick prompts"
                  data-tour="quick-prompts"
                >
                  <Zap />
                </Button>
              </Tooltip>
              <span data-tour="skills">
                <SkillsPopover
                  pinnedSkillIds={pinnedSkillIds}
                  setPinnedSkillIds={setPinnedSkillIds}
                  disabled={running}
                />
              </span>
            </>
          }
        />
      </div>
      <CommandPalette
        open={paletteOpen}
        onOpenChange={setPaletteOpen}
        starters={STARTERS}
        onPickPrompt={(prompt) => {
          void send(prompt, []);
        }}
        onShowKeymap={() => {
          setKeymapOpen(true);
        }}
        running={running}
      />
      <KeymapOverlay open={keymapOpen} onOpenChange={setKeymapOpen} />
    </div>
  );
}

export type Starter = {
  icon: ComponentType<{ className?: string }>;
  label: string;
  prompt: string;
};

// Empty-thread starter prompts. Each maps to a system skill the agent will
// load via use_skill once it sees the matching intent in the prompt text.
const STARTERS: Starter[] = [
  { icon: FileText, label: "Summarize deck", prompt: "Summarize this deck." },
  { icon: Flame, label: "Grill my deck", prompt: "Grill my deck." },
  { icon: Flame, label: "Grill my slide", prompt: "Grill my current slide." },
  {
    icon: Heading,
    label: "Rewrite titles as action titles",
    prompt: "Audit and rewrite every slide title as a partner-grade action title.",
  },
  { icon: MessageSquare, label: "Address comments in deck", prompt: "Solve the comments in my deck." },
  { icon: Scale, label: "Consistency check", prompt: "Run a consistency check across the deck." },
  { icon: EyeOff, label: "Anonymize deck", prompt: "Anonymize this deck." },
  { icon: Languages, label: "Translate deck", prompt: "Translate this deck." },
  { icon: Palette, label: "Enforce brand", prompt: "Enforce my brand and template across the deck." },
];

const SAMPLE_PROMPTS = [
  "create a pitch deck for a startup called Acme",
  "grill my deck like a skeptical partner",
  "rewrite every title as an action title",
  "restructure this into an executive storyline",
  "translate this deck to French",
  "anonymize all client names in this deck",
  "enforce my brand and template across the deck",
  "solve the comments in my deck",
  "build a chart from the attached Excel file",
  "create a skill for our house writing style",
] as const;

const SAMPLE_HOLD_MS = 4200;
const SAMPLE_OUT_MS = 220;

function EmptyState({
  onPick,
  disabled,
}: {
  onPick: (prompt: string) => void;
  disabled: boolean;
}) {
  const [sampleIndex, setSampleIndex] = useState(() =>
    Math.floor(Math.random() * SAMPLE_PROMPTS.length),
  );
  const [samplePhase, setSamplePhase] = useState<"in" | "out">("in");
  const sample = SAMPLE_PROMPTS[sampleIndex] ?? SAMPLE_PROMPTS[0];

  useEffect(() => {
    let cancelled = false;
    let timer = 0;

    const tick = (): void => {
      timer = window.setTimeout(() => {
        if (cancelled) return;
        setSamplePhase("out");
        timer = window.setTimeout(() => {
          if (cancelled) return;
          setSampleIndex((i) => (i + 1) % SAMPLE_PROMPTS.length);
          setSamplePhase("in");
          tick();
        }, SAMPLE_OUT_MS);
      }, SAMPLE_HOLD_MS);
    };

    tick();
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, []);

  return (
    <div className="relative flex flex-1 flex-col items-center justify-center gap-4 px-4 text-center overflow-hidden">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(circle_at_50%_20%,var(--color-primary)/6%,transparent_60%)]"
      />
      <div className="flex flex-col items-center gap-1 text-[var(--color-muted-foreground)]">
        <p className="flex min-h-[2.75rem] items-center justify-center gap-1 text-sm">
          <span className="shrink-0">Try</span>
          <button
            key={sampleIndex}
            type="button"
            disabled={disabled}
            onClick={() => {
              onPick(sample);
            }}
            className={cn(
              "max-w-[18rem] italic text-[var(--color-foreground)] underline-offset-2 hover:underline disabled:pointer-events-none disabled:opacity-50",
              samplePhase === "in" ? "animate-sample-in" : "animate-sample-out",
            )}
          >
            {sample}
          </button>
        </p>
        <p className="text-[11px]">
          …or drag a file in, or pick one below. Press{" "}
          <kbd className="font-mono rounded bg-[var(--color-secondary)] px-1 py-0.5 text-[10px]">
            {MOD_KEY}K
          </kbd>{" "}
          for the command palette.
        </p>
      </div>
      <Suggestions className="w-full max-w-sm flex-col gap-1.5">
        {STARTERS.map((s, i) => (
          <Suggestion
            key={s.label}
            variant="row"
            icon={s.icon}
            disabled={disabled}
            onClick={() => {
              onPick(s.prompt);
            }}
            style={{ animationDelay: `${i * 40}ms` }}
            className="animate-msg-in"
          >
            {s.label}
          </Suggestion>
        ))}
      </Suggestions>
    </div>
  );
}

function Pane({
  children,
  fileDrop,
}: {
  children: ReactNode;
  fileDrop?: {
    disabled: boolean;
    onFiles: (files: FileList) => void;
  };
}) {
  const [dragOver, setDragOver] = useState(false);
  const dragDepthRef = useRef(0);

  const clearDrag = () => {
    dragDepthRef.current = 0;
    setDragOver(false);
  };

  return (
    <div
      className="relative flex h-screen flex-col p-2 text-sm text-[var(--color-foreground)] bg-[var(--color-background)]"
      onDragEnter={(e) => {
        if (!fileDrop || fileDrop.disabled) return;
        if (!e.dataTransfer.types.includes("Files")) return;
        e.preventDefault();
        dragDepthRef.current += 1;
        setDragOver(true);
      }}
      onDragOver={(e) => {
        if (!fileDrop || fileDrop.disabled) return;
        if (!e.dataTransfer.types.includes("Files")) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "copy";
      }}
      onDragLeave={(e) => {
        if (!fileDrop || fileDrop.disabled) return;
        e.preventDefault();
        dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
        if (dragDepthRef.current === 0) setDragOver(false);
      }}
      onDrop={(e) => {
        if (!fileDrop || fileDrop.disabled) return;
        e.preventDefault();
        clearDrag();
        if (e.dataTransfer.files.length > 0) fileDrop.onFiles(e.dataTransfer.files);
      }}
    >
      {dragOver && (
        <div className="pointer-events-none absolute inset-2 z-50 flex items-center justify-center rounded-xl border-2 border-dashed border-[var(--color-primary)] bg-[var(--color-background)]/85 text-sm font-medium text-[var(--color-foreground)] backdrop-blur-[2px]">
          Drop files to attach
        </div>
      )}
      {children}
    </div>
  );
}
